// Types
import Alt from '@/types/models/alt';
import Comparison from '@/types/comparison';
import ComparisonItem from '@/types/comparison-item';

// Utils
import { addCommonInceptionToPerformance } from './shared';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { getComparisonAltDisclaimers } from '@/utils/disclaimers/alts';
import { HOLDING_TYPES } from '@/utils/portfolios';
import { getValidatedCountry } from '@/utils/domicile';
import { getAltAssetAllocations } from '@/utils/alts';

type Options = {
  shortHistory?: boolean;
  itemFlags?: string[];
  comparison?: Comparison;
};

const altToComparisonItem = (alt: Alt, options?: Options): ComparisonItem => {
  const {
    _id,
    names,
    currency,
    called,
    commitment,
    distributed,
    remaining,
    netIrr,
    medianBenchmarkNetIrr,
    performance: altPerformance,
    performanceBase: altPerformanceBase,
    classification,
    objective,
    manufacturer,
    domicile,
    managementFee,
    hurdleRate,
    gpCatchUpRate,
    carriedInterest,
    dates,
    fundFormationCost,
    chargeFrequency,
    dataProviders,
    status,
    quartile,
    vintage,
    hasPerformanceFromBenchmark,
  } = alt;

  const commonInceptionStart = options?.comparison?.commonInception?.startTimestamp;
  const disclaimers = getComparisonAltDisclaimers(alt);
  const performance = addCommonInceptionToPerformance(altPerformance, commonInceptionStart);
  const performanceBase = addCommonInceptionToPerformance(altPerformanceBase || altPerformance, commonInceptionStart);

  return {
    _id,
    allocations: {
      asset: getAltAssetAllocations(),
      country: [
        {
          name: getValidatedCountry(domicile),
          weight: 100,
        },
      ],
    },
    called,
    commitment: {
      size: commitment?.size,
      gpCommitmentPercent: commitment?.gpCommitmentPercent,
      gpCommitmentValue: commitment?.gpCommitmentValue,
    },
    carriedInterest: {
      rate: carriedInterest?.rate,
    },
    chargeFrequency,
    classification,
    currency,
    distributed,
    dataProviders,
    dates,
    disclaimers,
    domicile,
    flags: options?.itemFlags || [],
    fundFormationCost,
    gpCatchUpRate,
    holdings: [
      {
        id: _id,
        name: names?.full,
        rolling: performance?.rolling,
        type: HOLDING_TYPES.ALT,
        weight: 100, // Alternatives typically represent a single holding with 100% weight
      },
    ],
    hurdleRate,
    managementFee,
    manufacturer,
    medianBenchmarkNetIrr,
    name: names?.full!,
    netIrr,
    objective,
    performance,
    performanceBase,
    quartile,
    remaining,
    status,
    type: COMPARISON_ITEM_TYPES.ALT,
    vintage,
    hasPerformanceFromBenchmark,
  };
};

export default altToComparisonItem;
